import {
  cloneElement,
  forwardRef,
  ReactElement,
  ReactNode,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "./DataLoader.css";
import { cloneDeep, getErrorComponents } from "services/util";
import Dialog from "components/ui/Dialog";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { AbortFn, StateType } from "./type";

type Props = {
  fetchData: (args: any, onSuccess: (res: any) => void, onError: (d: any) => void) => any;
  preventAbortPreviousRequest?: boolean;
  fetchArgs?: { [key: string]: any };
  callback?: (data: { [key: string]: any }) => void;
  asDialog?: boolean;
  nullable?: boolean;
  children: ReactNode;
  refresh?: boolean;
  dataPropName?: string;
  hideSpinner?: boolean;
  reactive?: boolean;
};

type RefType = {
  getData: () => any;
};

const DataLoader = forwardRef(function DataLoader(
  props: Props,
  forwardedRef: Ref<RefType>
) {
  const { t } = useTranslation();
  const varRef = useRef({ refresh: false });
  const [state, setState] = useState<StateType>({
    loading: true,
    data: null,
    error: null,
    previousRequest: null,
  });

  const load = (args?: { [key: string]: any }, fetchData?: Props["fetchData"]) => {
    fetchData = fetchData || props.fetchData;
    if (!fetchData) {
      return;
    }

    if (!props.preventAbortPreviousRequest && state.loading) {
      if ((state.previousRequest as AbortFn)?.abort) {
        (state.previousRequest as AbortFn).abort();
      }
    }

    const request = fetchData(
      args ? args : props.fetchArgs || null,
      data => {
        setState(prev => ({
          ...prev,
          loading: false,
          data: data,
          error: null,
        }));

        varRef.current.refresh = false;
        !!props.callback && props.callback(data);
      },
      error => {
        setState(prev => ({ ...prev, loading: false, error: error }));
        varRef.current.refresh = false;
      }
    );

    setState(prev => ({ ...prev, previousRequest: request }));
  };

  useEffect(
    () => load(),
    // This is super ugly but it's the simplest way to make DataLoader reactive
    // to fetchArgs changes while preserving backward compatibility.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    props.reactive ? [props.fetchArgs] : []
  );

  useEffect(() => {
    if (props.refresh) {
      if (props.refresh !== varRef.current?.refresh) {
        varRef.current.refresh = props.refresh;
      }

      setState(prev => ({
        ...prev,
        loading: !props.hideSpinner,
        error: null,
      }));

      load(props.fetchArgs, props.fetchData);
    }
    // eslint-disable-next-line
  }, [props.fetchData, props.refresh, props.fetchArgs, props.hideSpinner]);

  useImperativeHandle(forwardedRef, () => ({
    getData() {
      return cloneDeep(state.data);
    },
  }));

  const spinner = props.asDialog ? (
    <Dialog
      open={state.loading}
      header={null}
      title={t("Common.status.loading")}
      onClose={() => undefined}
      closeOnOverlayClick={false}
      size="sm"
      className="data-loader-spinner-dialog"
    >
      <TalaeraSpinner className="spinner" />
    </Dialog>
  ) : (
    <div className="data-loader spinner-container">
      <TalaeraSpinner />
    </div>
  );

  if (state.loading) {
    return spinner;
  }

  if (state.error !== null) {
    return <div>{getErrorComponents(state.error)}</div>;
  }

  if ((state.data || (props.nullable && state.data === null)) && props.children) {
    return cloneElement(props.children as ReactElement<ReactNode>, {
      [props.dataPropName || "data"]: state.data,
    });
  }

  return <div>{getErrorComponents("response is empty or contains unexpected null")}</div>;
});

export type LegacyFetcher<TArgs, TResponse> = (
  args: TArgs,
  onSuccess: (res: TResponse) => void,
  onError: (e: any) => void
) => void;

export function toLegacyFetcher<TArgs, TResponse>(
  promiseFetcher: (args: TArgs) => Promise<TResponse>
): LegacyFetcher<TArgs, TResponse> {
  return (args, onSuccess, onError) => {
    promiseFetcher(args).then(onSuccess, onError);
  };
}

export default DataLoader;
